import { FC } from 'react';
import { Box, Divider, Text } from '@hopskipdrive/react-components';

import { useTimezone } from '../../../../hooks/useTimezone';

import { FindSuggestionsButton } from './FindSuggestionsButton';
import { NoSuggestions } from './NoSuggestions';
import { Suggestion } from './Suggestion';

interface SmartPoolingTabProps {
  enableFindSuggestions: boolean;
  onFindSuggestions: () => void;
  isSearching: boolean;
  suggestions: Suggestion[];
  handleSuggestionClick: (slug: string, analyticsData) => void;
}

export const SmartPoolingTab: FC<SmartPoolingTabProps> = ({
  enableFindSuggestions,
  onFindSuggestions,
  isSearching,
  suggestions,
  handleSuggestionClick,
}) => {
  const { formatInTimezone } = useTimezone();

  return suggestions?.length ? (
    <Box data-testid="smartpooling-tab">
      <Box display="flex" gap="24px" mb="24px" alignItems="center">
        <FindSuggestionsButton
          onClick={onFindSuggestions}
          disabled={!enableFindSuggestions || isSearching}
          isSearching={isSearching}
        />
        {isSearching && (
          <Text variant="labelM" fontWeight={500} textColor="secondary-active">
            Please wait while we look for the best matches for your selected ride series details
          </Text>
        )}
        {!suggestions[0]?.slug && !isSearching && (
          <Text variant="labelM" fontWeight={500} textColor="secondary-active">
            If you’re done setting up the ride use this button to view existing ride series that can help you save on
            rider fares.
          </Text>
        )}
        {suggestions[0]?.slug && !isSearching && (
          <Text variant="labelM" fontWeight={500} textColor="secondary-active">
            Click on a ride to open it in a new tab. You can also try changing any parameter from the ride series
            details and searching again.
          </Text>
        )}
      </Box>
      <Suggestion
        title="Best option"
        anchorTime={
          suggestions[0]?.anchorTime
            ? formatAnchorTimeText(
                routeTypeMap.get(suggestions[0].anchor),
                formatInTimezone(suggestions[0].anchorTime, 'h:mm aa')
              )
            : undefined
        }
        riderCount={suggestions[0]?.riderCount}
        durationInMinutes={secondsToMinutes(suggestions[0]?.duration)}
        riders={suggestions[0]?.riders}
        disabled={!suggestions[0]?.slug}
        onClick={() => {
          handleSuggestionClick(suggestions[0]?.slug, { index: 1, riderCount: suggestions[0]?.riderCount });
        }}
      />
      <Divider my="12px" />
      <Suggestion
        title="Second best"
        anchorTime={
          suggestions[1]?.anchorTime
            ? formatAnchorTimeText(
                routeTypeMap.get(suggestions[1].anchor),
                formatInTimezone(suggestions[1].anchorTime, 'h:mm aa')
              )
            : undefined
        }
        riderCount={suggestions[1]?.riderCount}
        durationInMinutes={secondsToMinutes(suggestions[1]?.duration)}
        riders={suggestions[1]?.riders}
        disabled={!suggestions[1]?.slug}
        onClick={() =>
          handleSuggestionClick(suggestions[1]?.slug, { index: 2, riderCount: suggestions[1]?.riderCount })
        }
      />
      <Divider my="12px" />
      <Suggestion
        title="Third best"
        anchorTime={
          suggestions[2]?.anchorTime
            ? formatAnchorTimeText(
                routeTypeMap.get(suggestions[2].anchor),
                formatInTimezone(suggestions[2].anchorTime, 'h:mm aa')
              )
            : undefined
        }
        durationInMinutes={secondsToMinutes(suggestions[2]?.duration)}
        riders={suggestions[2]?.riders}
        riderCount={suggestions[2]?.riderCount}
        disabled={!suggestions[2]?.slug}
        onClick={() => {
          handleSuggestionClick(suggestions[2]?.slug, { index: 3, riderCount: suggestions[2]?.riderCount });
        }}
      />
      <Divider my="12px" />
    </Box>
  ) : (
    <NoSuggestions />
  );
};

const formatAnchorTimeText = (routeType, anchorTime) => {
  return `${routeType} at ${anchorTime}`;
};

const routeTypeMap = new Map([
  ['pick_up', 'Pickup'],
  ['drop_off', 'Drop-off'],
]);

const secondsToMinutes = (seconds) => {
  return seconds ? Math.floor(seconds / 60) : 0;
};
